import { PayloadAction } from '@reduxjs/toolkit';
import { put, takeLatest, retry } from '@redux-saga/core/effects';
import { error } from '@innovyze/stylovyze';
import { AxiosResponse } from 'axios';
import type { Row } from '@innovyze/lib-am-common';
import {
  listCostTablesAction,
  listCostTableActionResolved,
  listCostTablesActionRejected,
  getCostTableDetailsAction,
  getCostTableDetailsActionResolved,
  getCostTableDetailsActionRejected,
} from '@Actions/costTables.actions';
import {
  GetCostTablesOverviewResponse,
  getCostTableOverviewData,
  GetCostTableDetailsResponse,
  getCostTableDetails,
} from '@Services/costTables.services';
import {
  GetCostTableGridPaginatedRequest,
  ApiError,
} from '@Types/costTable.types';

function* listCostTablesOverviewSaga(
  action: PayloadAction<GetCostTableGridPaginatedRequest>
) {
  try {
    const payload: AxiosResponse<GetCostTablesOverviewResponse> = yield retry(
      5,
      15000,
      getCostTableOverviewData,
      action.payload
    );

    yield put(
      listCostTableActionResolved({
        pagination: {
          ...payload.data.pagination,
          subtotal: payload.data.pagination.total,
        },
        totalCount: payload.data.totalCount,
        data: payload.data.data.map(costTable => ({
          id: costTable.id,
          name: costTable.name,
          assetGroupSet: costTable.asset_group_set,
          costPer: costTable.cost_per,
          lastUpdated: costTable.last_updated,
        })) as Row[],
      })
    );
  } catch (e) {
    console.error('Error retrieving cost tables:', e);
    const apiError = e as ApiError;
    const { response } = apiError;
    yield put(
      listCostTablesActionRejected({
        code: response?.status ?? -1,
        text: response?.statusText ?? 'No additional information',
      })
    );
    yield put(error('Unable to list cost tables'));
  }
}

function* getCostTableDetailsSaga(
  action: PayloadAction<{ costTableId: string }>
) {
  try {
    const payload: AxiosResponse<GetCostTableDetailsResponse> = yield retry(
      5,
      15000,
      getCostTableDetails,
      action.payload.costTableId
    );

    yield put(
      getCostTableDetailsActionResolved({
        pagination: {
          page: 1,
          slice: payload.data.cost_table_data.length,
          total: payload.data.cost_table_data.length,
          pages: 1,
          subtotal: payload.data.cost_table_data.length,
        },
        data: payload.data as any,
      })
    );
  } catch (e) {
    console.error('Error retrieving cost table details:', e);
    const apiError = e as ApiError;
    const { response } = apiError;
    yield put(
      getCostTableDetailsActionRejected({
        code: response?.status ?? -1,
        text: response?.statusText ?? 'No additional information',
      })
    );
    yield put(error('Unable to load cost table details'));
  }
}

function* watchCostTablesOverviewSaga() {
  yield takeLatest(listCostTablesAction, listCostTablesOverviewSaga);
}

function* watchCostTableDetailsSaga() {
  yield takeLatest(getCostTableDetailsAction, getCostTableDetailsSaga);
}

export default [watchCostTablesOverviewSaga, watchCostTableDetailsSaga];
